import * as Tone from 'tone';
import { PlaybackState, TimeClass } from 'tone';
import { Transport } from 'tone/build/esm/core/clock/Transport';
import { Seconds, TransportTime } from 'tone/build/esm/core/type/Units';
import { sequences } from '@magenta/music/es6/core';
import { STEPS_PER_QUARTER, LOOK_AHEAD } from '../constants';
import { clamp } from './math-utils';
import { Progress } from './types';

export default class MalakaiTransport {
  transport: Transport;

  duration: TimeClass;

  get state(): PlaybackState {
    return this.transport.state;
  }

  get lookAhead(): Seconds {
    return this.transport.context.lookAhead;
  }

  get tempo(): number {
    return this.transport.bpm.value;
  }

  set tempo(bpm: number) {
    this.transport.bpm.value = bpm;
  }

  get stepsPerSecond(): number {
    return sequences.stepsPerQuarterToStepsPerSecond(STEPS_PER_QUARTER, this.tempo);
  }

  /** Malakai's Tone.Transport time without lookAhead */
  get playbackTime(): Seconds {
    return this.transport.seconds;
  }

  get progress(): Progress {
    return this.getProgressForTime(this.playbackTime);
  }

  constructor(duration: TransportTime, tempo: number) {
    this.transport = Tone.Transport;
    this.transport.context.lookAhead = LOOK_AHEAD;
    this.tempo = tempo;
    this.duration = Tone.Time(duration);
  }

  start() {
    if (this.state === 'stopped') {
      Tone.Destination.mute = false;
      Tone.start();
      Tone.context.resume();
      this.transport.start();
    }
  }

  pause() {
    if (this.state === 'started') {
      Tone.Destination.mute = true;
      this.transport.pause();
    }
  }

  resume() {
    if (this.state === 'paused') {
      Tone.Destination.mute = false;
      this.transport.start();
    }
  }

  stop() {
    if (this.state !== 'stopped') {
      Tone.Destination.mute = true;
      // Stop playback
      this.transport.stop();
      // Clear scheduled events
      this.transport.cancel();
    }
  }

  setPosition(time: TransportTime) {
    this.transport.seconds = clamp(
      Tone.Time(time).toSeconds(),
      0,
      this.duration.toSeconds(),
    );
  }

  /** Get song progress for the given time. */
  getProgressForTime(time: TransportTime): Progress {
    // TODO: Add option to exclude lookahead
    return clamp(Tone.Time(time).toSeconds() / this.duration.toSeconds(), 0, 1);
  }

  /** Get playback time for the given progress. */
  getTimeForProgress(progress: Progress): Seconds {
    return this.duration.toSeconds() * progress;
  }

  /** Convert ToneJS callback time to transport time */
  secondsToTransportTime(seconds: Tone.Unit.Time, doubleLookAhead = false): number {
    const lookAhead = this.lookAhead * (doubleLookAhead ? 2 : 1);
    return this.transport.getSecondsAtTime(seconds) + lookAhead;
  }

  /** Trigger an callback at a time along the transport without lookAhead */
  trigger(callback: () => void, time: Seconds) {
    this.transport.scheduleOnce(() => callback(), time);
  }

  scheduleOnce(
    callback: (time: Seconds) => void,
    time: TransportTime,
  ) {
    this.transport.scheduleOnce((t) => {
      callback(this.secondsToTransportTime(t, true));
    }, Tone.Time(time).toSeconds());
  }

  scheduleRepeat(
    callback: (time: Seconds) => void,
    start: TransportTime,
    interval: TransportTime,
    iterations?: number,
  ) {
    const loop = new Tone.Loop({
      interval: Tone.Time(interval).toSeconds(),
      iterations: iterations || Infinity,
      callback: (t) => {
        callback(this.secondsToTransportTime(t, true));
      },
    });
    loop.start(Tone.Time(start).toSeconds());
  }
}
